import { Component, OnInit, OnDestroy } from '@angular/core';
import { ComponentsService } from 'src/app/components.service';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatTableDataSource } from '@angular/material/table';
import { Subscription } from 'rxjs';

@Component({
    selector: 'app-blocked-players',
    templateUrl: './blocked-players.component.html',
    styleUrls: ['./blocked-players.component.scss']
})
export class BlockedPlayersComponent implements OnInit, OnDestroy {
    selectedUserUuid: string = '';
    statusFilter: number | null = null;
    blocked: boolean = false;
    unblocked: boolean = false;
    private currentFilter: { userUuid: string; status: number | null } = {
        userUuid: '',
        status: null
    };
    perms: any;
    userForm: FormGroup;
    addUserClicked: boolean = false;
    isBlockClicked: boolean = false;
    isUnblockClicked: boolean = false;
    dataSourceblockedPlayersTable: MatTableDataSource<blockedPlayersTable>;
    columnsBlockedPlayers: string[] = [
        'status',
        'userUuid',
        'reasonForBlocking',
        'notes',
        'blockDateTime',
        'blockedBy',
        'unblockDateTime',
        'unblockedBy'
    ];
    selectedRowId: number;
    editMode: boolean = false;
    displayMessage: boolean = false;
    message: string;
    hideBtn: boolean = false;
    clientName: string;
    private clientChangeSubscription: Subscription;

    constructor(
        private service: ComponentsService,
        private fb: FormBuilder
    ) {}

    ngOnInit(): void {
        this.perms = this.service.getUsersPerm();

        this.service.getClientName(this.perms.clientUuid).subscribe((data) => {
            this.clientName = data.data;
        });

        this.getBlockedPlayers();

        this.userForm = this.fb.group({
            userUuid: [null, Validators.required],
            reasonForBlocking: [null, Validators.required],
            notes: [''],
            status: [],
            clientName: [this.clientName, Validators.required],
            blockDateTime: [null],
            unblockDateTime: [null],
            blockedBy: [this.perms.username],
            unblockedBy: [null]
        });

        if (this.service.getUsersPerm().superAdmin == 1) {
            this.clientChangeSubscription =
                this.service.clientChanged.subscribe(async (client) => {
                    this.perms.clientUuid = client.clientUuid;

                    this.service
                        .getClientName(client.clientUuid)
                        .subscribe((data) => {
                            this.clientName = data.data;
                            this.userForm.patchValue({
                                clientName: this.clientName
                            });
                        });

                    this.cancel();
                    this.clearFilters();
                    await this.getBlockedPlayers();
                });
        } else {
            this.clientChangeSubscription =
                this.service.clientChanged.subscribe(async (client) => {
                    this.perms.clientUuid = client.clientUuid;
                    this.service
                        .getClientName(client.clientUuid)
                        .subscribe((data) => {
                            this.clientName = data.data;
                            this.userForm.patchValue({
                                clientName: this.clientName
                            });
                        });

                    this.cancel();
                    this.clearFilters();
                    await this.getBlockedPlayers();
                });
        }
    }

    ngOnDestroy(): void {
        if (this.clientChangeSubscription) {
            this.clientChangeSubscription.unsubscribe();
        }
    }

    async getBlockedPlayers() {
        const data = await this.service.getBlockedPlayers(
            this.perms.clientUuid
        );

        this.dataSourceblockedPlayersTable =
            new MatTableDataSource<blockedPlayersTable>(data);
        this.dataSourceblockedPlayersTable.filterPredicate = (
            d: blockedPlayersTable,
            filter: string
        ) => {
            const searchTerm = JSON.parse(filter);
            const matchesUserUuid = searchTerm.userUuid
                ? d.userUuid.includes(searchTerm.userUuid)
                : true;
            const matchesStatus =
                searchTerm.status !== null
                    ? d.status === searchTerm.status
                    : true;
            return matchesUserUuid && matchesStatus;
        };
    }

    checkUserUuidExists(userUuid: string): boolean {
        const existingUser = this.dataSourceblockedPlayersTable.data.find(
            (user) => user.userUuid === userUuid
        );
        if (existingUser) {
            this.displayMessage = true;
            this.message = 'User exists already';
            this.fadeOutMessage();
            return true; // Korisnik postoji
        }
        return false; // Korisnik ne postoji
    }

    toggleAddUser() {
        this.addUserClicked = !this.addUserClicked;
        if (this.addUserClicked) {
            this.userForm.enable();
            this.userForm.reset();
            this.userForm.patchValue({
                clientName: this.clientName
            });
        }
    }

    addUser() {
        this.addUserClicked = true;
        this.userForm.enable();
        this.userForm.reset();
        this.userForm.patchValue({
            clientName: this.clientName
        });
    }

    blockUser() {
        if (this.userForm.invalid) {
            this.userForm.markAllAsTouched();
            return;
        }

        if (!this.editMode) {
            let userUuid = this.userForm.get('userUuid').value;
            if (this.checkUserUuidExists(userUuid)) {
                return; // Prekini dalji tok ako korisnik već postoji
            }
        }
        if (this.userForm.valid) {
            this.userForm.patchValue({
                status: 1,
                blockDateTime: new Date(),
                blockedBy: this.perms.username
            });
            this.isBlockClicked = true;
            this.isUnblockClicked = false;

            this.onSubmit();
        }
    }

    unblockUser() {
        if (this.userForm.invalid) {
            this.userForm.markAllAsTouched();
            return;
        }

        if (!this.editMode) {
            let userUuid = this.userForm.get('userUuid').value;
            if (this.checkUserUuidExists(userUuid)) {
                return; // Prekini dalji tok ako korisnik već postoji
            }
        }
        if (this.userForm.valid) {
            this.userForm.patchValue({
                status: 0,
                unblockDateTime: new Date(),
                unblockedBy: this.perms.username
            });
            this.isBlockClicked = false;
            this.isUnblockClicked = true;

            this.onSubmit();
        }
    }

    async onSubmit() {
        if (this.userForm.valid) {
            if (this.editMode) {
                if (this.isBlockClicked) {
                    this.userForm.patchValue({
                        status: 1,
                        blockDateTime: new Date(),
                        blockedBy: this.perms.username,
                        unblockDateTime: null,
                        unblockedBy: null
                    });
                } else if (this.isUnblockClicked) {
                    this.userForm.patchValue({
                        status: 0,
                        unblockDateTime: new Date(),
                        unblockedBy: this.perms.username,
                        blockDateTime: null,
                        blockedBy: null
                    });
                }

                // Ažuriranje korisnika
                try {
                    await this.service.updateBlockedPlayer(this.userForm.value);
                    this.getBlockedPlayers(); // Osvježavanje tabele
                    this.cancel(); // Resetovanje forme nakon uspešnog ažuriranja
                } catch (error) {
                    alert('Update failed');
                } finally {
                }
            } else {
                try {
                    await this.service.insertBlockedPlayer(this.userForm.value);
                    this.getBlockedPlayers(); // Osvježavanje tabele
                    this.cancel(); // Resetovanje forme nakon uspešnog slanja
                } catch (error) {
                    alert('Insert failed');
                }
            }
        }
    }

    cancel() {
        this.editMode = false;
        this.addUserClicked = false;
        this.hideBtn = false;
        this.selectedRowId = null;
        this.userForm.reset({
            clientName: this.clientName,
            blockedBy: this.perms.username,
            unblockedBy: null
        });

        this.isBlockClicked = false;
        this.isUnblockClicked = false;
    }

    onPaste(data: any) {
        data.target.value = data.target.value.trim();
    }

    applyStatusFilter(statusType: string, event: any) {
        if (statusType === 'blocked') {
            this.blocked = event.checked;
            if (this.blocked) {
                this.unblocked = false;
            }
        } else if (statusType === 'unblocked') {
            this.unblocked = event.checked;
            if (this.unblocked) {
                this.blocked = false;
            }
        }
    }

    applyFilters() {
        this.currentFilter.userUuid = this.selectedUserUuid.trim();
        this.currentFilter.status = this.statusFilter;
        if (this.dataSourceblockedPlayersTable) {
            this.dataSourceblockedPlayersTable.filter = JSON.stringify(
                this.currentFilter
            );
        }
    }

    clearFilters() {
        this.selectedUserUuid = '';
        this.statusFilter = null;
        this.currentFilter = { userUuid: '', status: null };
        if (this.dataSourceblockedPlayersTable) {
            this.dataSourceblockedPlayersTable.filter = '';
        }
    }

    searchByUserId() {
        this.currentFilter.userUuid = this.selectedUserUuid.trim();
        if (this.dataSourceblockedPlayersTable) {
            this.dataSourceblockedPlayersTable.filter = JSON.stringify(
                this.currentFilter
            );
        }
    }

    searchByStatus() {
        this.currentFilter.status = this.blocked
            ? 1
            : this.unblocked
              ? 0
              : null;
        if (this.dataSourceblockedPlayersTable) {
            this.dataSourceblockedPlayersTable.filter = JSON.stringify(
                this.currentFilter
            );
        }
    }

    highlightRow(row: blockedPlayersTable) {
        if (this.selectedRowId === row.id) {
            this.selectedRowId = null;
            this.cancel();
        } else {
            this.selectedRowId = row.id;
            this.editSelectedUser();
        }
    }

    editSelectedUser() {
        if (this.selectedRowId !== null && this.selectedRowId !== undefined) {
            const selectedRow = this.dataSourceblockedPlayersTable.data.find(
                (row) => row.id === this.selectedRowId
            );
            if (selectedRow) {
                this.userForm.patchValue({
                    userUuid: selectedRow.userUuid,
                    reasonForBlocking: selectedRow.reasonForBlocking,
                    notes: selectedRow.notes,
                    status: selectedRow.status,
                    blockDateTime: selectedRow.blockDateTime,
                    unblockDateTime: selectedRow.unblockDateTime,
                    blockedBy: selectedRow.blockedBy,
                    unblockedBy: selectedRow.unblockedBy,
                    clientName: this.clientName
                });
                this.hideBtn = true;
                this.editMode = true;
            }
        } else {
            this.displayMessage = true;
            this.message = `Please select a user to edit.`;
            this.fadeOutMessage();
        }
    }

    fadeOutMessage() {
        if (this.displayMessage) {
            setTimeout(() => {
                this.displayMessage = false;
            }, 5000);
        }
    }

    refreshAndResetFilters() {
        this.unblocked = false;
        this.blocked = false;
        this.selectedUserUuid = '';
        this.statusFilter = null;
        this.currentFilter = { userUuid: '', status: null };
        this.getBlockedPlayers();
        if (this.dataSourceblockedPlayersTable) {
            this.dataSourceblockedPlayersTable.filter = '';
        }
    }

    toggleReason(element: blockedPlayersTable) {
        element.isReasonExpanded = !element.isReasonExpanded;
    }

    toggleNotes(element: blockedPlayersTable) {
        element.isNotesExpanded = !element.isNotesExpanded;
    }
}

export interface blockedPlayersTable {
    id: number;
    userUuid: string;
    clientName: string;
    status: number;
    reasonForBlocking: string;
    notes: string;
    blockDateTime: string;
    unblockDateTime: string;
    blockedBy: string;
    unblockedBy: string;
    isReasonExpanded?: boolean; // Add these properties to track expanded state
    isNotesExpanded?: boolean;
}
